import React, { useState } from 'react';
import { Button, IconButton, makeStyles } from '@material-ui/core';
import CallIcon from '@material-ui/icons/Call';
import SyncAltIcon from '@material-ui/icons/SyncAlt';

import DialpadIcon from '@material-ui/icons/Dialpad';
import MicOffIcon from '@material-ui/icons/MicOff';
import PauseIcon from '@material-ui/icons/Pause';
import { RecordingIcon } from '../../GlobalAddDealModalV3/components/Icons';
import { useDispatch, useSelector } from "react-redux";
import CountTimer from "./CountTimer";
import SendDigitKeypad from "./SendDigitKeypad";
import { CALL_RECORDING_STATUS, GLOBAL_DIALER_CALL_STATUS } from "./index";
import {
    resetContacts,
    setCallEnvironment,
    setGlobalDialerState
} from "../../../reducers/globalDialerV2/globalDialerSlice";
import Utils from "../../../helpers/Utils";
import ScrollText from "../../custom/ScrollText";
import BootstrapTooltip from "../../common/BootstrapTooltip";
import { updateRecordStatus } from "../../../api/globalDialerApi";
import { GLOBAL_DIALER_TYPES } from "../../../helpers/CoreConstant";

const useStyles = makeStyles((theme) => ({
    callWrapper: {
        color: '#fff',
        "& .headerSection": {
            display: 'flex !important',
            justifyContent: 'space-between !important',
            alignItems: 'center !important',
            padding: '12px !important',
            paddingLeft: '0px !important',
            paddingTop: '4px !important',
            borderBottom: '1px solid #e5e8ef !important',

            '&.borderLessHeader': {
                border: 'none !important',
            },

            "& .headerSectionLeft": {
                display: 'flex !important',
                alignItems: 'center !important',
                gap: '8px !important',

                '& .MuiSvgIcon-root': {
                    color: '#fff !important'
                }
            },

            "& .headerSectionRight": {
                display: "flex",
                flexDirection: "column",
                alignItems: "end",

                "& .callStatus": {
                    borderRadius: "10px",
                    textAlign: "center",
                    fontSize: "12px",
                    width: "fit-content",
                    padding: "0px 10px",
                    marginBottom: "4px",

                    "&.calling": {
                        backgroundColor: "#f8d300ff",
                        color: "#000"
                    },

                    "&.inCall": {
                        backgroundColor: "#33f28b",
                        color: "#000"
                    }
                }
            }
        },

        "& .contentSection": {
            padding: "20px 12px !important",

            "& .iconList": {
                display: 'flex !important',
                justifyContent: 'space-between !important',
                alignItems: 'center !important',

                "& .eachIcon": {
                    position: 'relative',
                    color: '#fff !important',
                    overflow: 'visible',
                    backgroundColor: 'transparent !important',

                    '&.recording::before': {
                        content: '""',
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        width: 48,
                        height: 48,
                        backgroundColor: 'rgba(255, 0, 0, 0.5)',
                        borderRadius: '50%',
                        transform: 'translate(-50%, -50%)',
                        animation: '$pulse 1.2s infinite ease-in-out',
                        zIndex: 0,
                    },

                    "&.active": {
                        color: '#f44336 !important',
                    },

                    "& svg": {
                        fontSize: '30px !important',
                        color: '#fff !important',
                    },

                    "&.recordingButton svg": {
                        position: 'relative',
                        zIndex: 1,
                    },

                    "&:hover, &:focus": {
                        backgroundColor: "transparent !important",
                    }
                },
            }
        }
    },
    endCallButton: {
      backgroundColor: "#f50057 !important",
      color: '#fff !important',
      fontSize: '14px !important',
      textTransform: 'capitalize !important',
      fontWeight: "600 !important",
      padding: '6px 16px !important',
      "&:hover, &:focus": {
          backgroundColor: "#f50057 !important",
          color: '#fff !important',
      }  
    },
    defaultButton: {
        backgroundColor: "#fff !important",
        "&:hover, &:focus": {
            backgroundColor: "#fff !important",
        }
    },
    '@keyframes pulse': {
        '0%': {
            transform: 'translate(-50%, -50%) scale(1)',
            opacity: 0.7,
        },
        '70%': {
            transform: 'translate(-50%, -50%) scale(1.8)',
            opacity: 0,
        },
        '100%': {
            transform: 'translate(-50%, -50%) scale(1.8)',
            opacity: 0,
        },
    },
}));

const CallConnected = ({
    callTime,
    rejectCall,
    muteCall,
    holdCall,
    sendDigits,
    resetGlobalDialer,
    onDialerTypeChange,
    setNumber
}) => {
    const dispatch = useDispatch();
    const { callEnvironment } = useSelector((state) => (state.globalDialerV2));
    const [callStatus, setCallStatus] = useState({
        isMuted: false,
        isPaused: false
    });
    const [showKeypad, setShowKeypad] = useState(false);

    const classes = useStyles();

    const handleCallTransfer = () => {
        dispatch(setCallEnvironment({
            isTransferring: true
        }));
        dispatch(resetContacts());
        resetGlobalDialer();
        onDialerTypeChange(GLOBAL_DIALER_TYPES.DEFAULT, true);
        setNumber("");
    };

    const handleShowKeypad = () => {
        setShowKeypad((prevState) => (!prevState));
    };

    const handleMuteCall = () => {
        muteCall(!callStatus.isMuted);
        setCallStatus((prevState) => ({
            ...prevState,
            isMuted: !prevState.isMuted
        }));
    };

    const handleHoldCall = async () => {
        const isPaused = callStatus.isPaused;

        setCallStatus((prevState) => ({
            ...prevState,
            isPaused: !prevState.isPaused
        }));

        await holdCall(callStatus.isPaused, (success) => {
            if (!success) {
                setCallStatus((prevState) => ({
                    ...prevState,
                    isPaused: isPaused
                }));
            }
        });
    };

    const handleCallRecord = async () => {
        if (callEnvironment.callRecordInstance > 0 &&
            callEnvironment.callRecordingStatus === CALL_RECORDING_STATUS.STOPPED) {
            window.showNotification('error', 'Recording for this call cannot be restarted once stopped.');
            return;
        }

        const recordStatus = callEnvironment.callRecordingStatus === CALL_RECORDING_STATUS.START ? CALL_RECORDING_STATUS.PAUSED : CALL_RECORDING_STATUS.START

        const response = await updateRecordStatus({
            recordStatus: recordStatus,
            callSid: callEnvironment.callInfo.firstCallSid,
            conferenceSid: callEnvironment.callInfo.conferenceSid,
        });

        if (response?.success) {
            dispatch(setCallEnvironment({
                callRecordInstance: callEnvironment.callRecordInstance + 1,
                callRecordingStatus: recordStatus
            }));
        }
    };

    const renderCallerInfo = () => {
        const view = [];

        if (callEnvironment.callInfo.callerName && !Utils.isNumeric(callEnvironment.callInfo.callerName)) {
            view.push(
                <ScrollText width={120}>
                    <h6 style={{ margin: '0px 4px 4px', fontSize: '15px' }}>{callEnvironment.callInfo.callerName}</h6>
                </ScrollText>
            );
        } else {
            view.push(<h6 style={{ margin: 0, fontSize: '15px' }}>Unknown</h6>);
        }

        if (callEnvironment.toNumber) {
            view.push(<h6 style={{ margin: 0, fontSize: '14px' }}>{Utils.formatNumber(callEnvironment.toNumber)}</h6>);
        } else if (callEnvironment.callInfo.callerNumber) {
            view.push(<h6 style={{ margin: 0, fontSize: '14px' }}>{Utils.formatNumber(callEnvironment.callInfo.callerNumber)}</h6>);
        }

        return (
            <div>
                {view}
            </div>
        );
    };

    const renderCallStatus = () => {
        if (callEnvironment.callStatus === GLOBAL_DIALER_CALL_STATUS.DIALING) {
            return <div className="callStatus calling">Calling...</div>;
        } else if (callEnvironment.callStatus === GLOBAL_DIALER_CALL_STATUS.RECEIVED) {
            return <div className="callStatus inCall">In Call...</div>;
        }
    };

    return (
        <div className={classes.callWrapper}>
            <div className={showKeypad ? 'headerSection borderLessHeader' : 'headerSection'}>
                <div className='headerSectionLeft'>
                    <CallIcon />

                    {renderCallerInfo()}
                </div>

                <div className="headerSectionRight">
                    {renderCallStatus()}
                    <CountTimer
                        callTime={callTime}
                        timeElapsedCallback={(timeElapsed) => {
                            dispatch(setGlobalDialerState({
                                lastCallTime: timeElapsed
                            }));
                        }}
                    />
                </div>
            </div>

            {
                showKeypad &&
                <SendDigitKeypad
                    onKeypress={(digit) => sendDigits(digit)}
                />
            }

            <div className='contentSection'>
                <div className='iconList'>
                    <BootstrapTooltip arrow placement="bottom" title="Mute">
                        <IconButton
                            className={`eachIcon ${callStatus.isMuted && "active"}`}
                            onClick={handleMuteCall}
                        >
                            <MicOffIcon />
                        </IconButton>
                    </BootstrapTooltip>

                    <BootstrapTooltip arrow placement="bottom" title="Dialpad">
                        <IconButton
                            className={`eachIcon ${showKeypad && "active"}`}
                            onClick={handleShowKeypad}
                        >
                            <DialpadIcon />
                        </IconButton>
                    </BootstrapTooltip>

                    <BootstrapTooltip arrow placement="bottom" title={callEnvironment.callRecordingStatus === CALL_RECORDING_STATUS.START ? "Stop Recording" : "Record"}>
                        <IconButton
                            className={`eachIcon recordingButton ${callEnvironment.callRecordingStatus === CALL_RECORDING_STATUS.START ? 'recording' : ''}`}
                            onClick={handleCallRecord}
                        >
                            <RecordingIcon height={30} width={30} />
                        </IconButton>
                    </BootstrapTooltip>

                    <BootstrapTooltip arrow placement="bottom" title="Hold">
                        <IconButton
                            className={`eachIcon ${callStatus.isPaused && "active"}`}
                            onClick={handleHoldCall}
                        >
                            <PauseIcon />
                        </IconButton>
                    </BootstrapTooltip>

                    <BootstrapTooltip arrow placement="bottom" title="Transfer Call">
                        <IconButton className='eachIcon' onClick={handleCallTransfer}>
                            <SyncAltIcon />
                        </IconButton>
                    </BootstrapTooltip>
                </div>
            </div>

            <div className='footerSection'
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8
                }}
            >
                <Button
                    variant='contained'
                    color='secondary'
                    disableElevation
                    fullWidth
                    className={classes.endCallButton}
                    onClick={rejectCall}
                >
                    End Call
                </Button>
            </div>
        </div>
    );
};

export default CallConnected;